import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { authorizedFetch, parseApiErrorMessage } from '@/lib/api'
import { API_ORIGIN } from '@/constants'
import { useAuth } from '@/hooks/use-auth'

export const organisationFollowQueryKey = (slug: string) =>
  ['organisation-follow', slug.trim()] as const

type OrganisationFollowResponse = {
  following: boolean
}

async function readFollowResponse(
  response: Response,
  fallback: string,
): Promise<OrganisationFollowResponse> {
  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response, fallback))
  }
  return response.json() as Promise<OrganisationFollowResponse>
}

export function useOrganisationFollow(slug: string, { enabled = true }: { enabled?: boolean } = {}) {
  const trimmed = slug.trim()
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const statusQuery = useQuery({
    queryKey: organisationFollowQueryKey(trimmed),
    queryFn: async (): Promise<OrganisationFollowResponse> => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/organisations/${encodeURIComponent(trimmed)}/follow/`,
      )
      return readFollowResponse(response, 'Could not load follow status')
    },
    enabled: enabled && Boolean(token) && Boolean(trimmed),
  })

  const followMutation = useMutation({
    mutationFn: async (nextFollowing: boolean) => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/organisations/${encodeURIComponent(trimmed)}/follow/`,
        { method: nextFollowing ? 'POST' : 'DELETE' },
      )
      return readFollowResponse(response, 'Could not update follow status')
    },
    onMutate: async (nextFollowing) => {
      const key = organisationFollowQueryKey(trimmed)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<OrganisationFollowResponse>(key)
      queryClient.setQueryData(key, { following: nextFollowing })
      return { previous }
    },
    onError: (error, _nextFollowing, context) => {
      queryClient.setQueryData(organisationFollowQueryKey(trimmed), context?.previous)
      const message = error instanceof Error ? error.message : 'Could not update follow status'
      toast.error(message)
    },
    onSuccess: (data) => {
      queryClient.setQueryData(organisationFollowQueryKey(trimmed), data)
    },
  })

  const following = statusQuery.data?.following ?? false

  return {
    following,
    isLoading: statusQuery.isLoading,
    isPending: followMutation.isPending,
    toggleFollow: () => followMutation.mutate(!following),
  }
}
